import React, { useEffect, useState } from "react";
import { Bar, Line } from "react-chartjs-2";
// New (react-icons) – using Tabler set or alternatives
import {
  TbPackage, // Tabler version inside react-icons
  TbShoppingCart,
  TbCurrencyRupee,
  TbClock,
  TbUsers,
  TbTrendingUp,
  TbAlertCircle,
} from "react-icons/tb"; // ← "tb" = Tabler Icons

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  CartesianGrid as ChartCartesianGrid, // optional but useful
} from "chart.js";
import performRequest from "../../../api/request";

// Register Chart.js components (do this once, usually in index.js or here)
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement
);

const orderData = [
  { name: "Mon", orders: 120, revenue: 145000 },
  { name: "Tue", orders: 210, revenue: 268000 },
  { name: "Wed", orders: 180, revenue: 210000 },
  { name: "Thu", orders: 250, revenue: 320000 },
  { name: "Fri", orders: 300, revenue: 410000 },
  { name: "Sat", orders: 400, revenue: 580000 },
  { name: "Sun", orders: 350, revenue: 490000 },
];

export default function Dashboard() {
  const [headerStats, setHeaderStats] = useState({});
  const [recentOrders, setRecentOrders] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [weekelyOrderData, setWeeklyOrderData] = useState([]);
  const getStatusBadgeClass = (status) => {
    console.log("Order status:", status); // Debug log
    if (status === "delivered") return "bg-success";
    if (status === "pending" || status === "Processing") return "bg-warning";
    if (status === "processing") return "bg-info";
    if (status === "cancel") return "bg-danger";
    return "bg-secondary";
  };

  // Chart.js data & options for Weekly Orders (Bar)
  const barChartData = {
    labels: weekelyOrderData?.map((d) => d.name),
    datasets: [
      {
        label: "Orders",
        data: weekelyOrderData?.map((d) => d.orders),
        backgroundColor: "rgba(32, 107, 196, 0.7)", // Tabler blue-ish
        borderColor: "#206bc4",
        borderWidth: 1,
        borderRadius: 4,
      },
      {
        label: "Revenue (₹)",
        data: weekelyOrderData.map((d) => d.revenue / 1000), // in thousands for better scale
        backgroundColor: "rgba(94, 186, 0, 0.7)", // Tabler green-ish
        borderColor: "#5eba00",
        borderWidth: 1,
        borderRadius: 4,
        yAxisID: "y1", // secondary axis
      },
    ],
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => {
            if (context.dataset.label === "Revenue (₹)") {
              return `${context.dataset.label}: ₹${(
                context.parsed.y * 1000
              ).toLocaleString()}`;
            }
            return `${context.dataset.label}: ${context.parsed.y}`;
          },
        },
      },
    },
    scales: {
      y: { beginAtZero: true, title: { display: true, text: "Orders" } },
      y1: {
        type: "linear",
        position: "right",
        beginAtZero: true,
        title: { display: true, text: "Revenue (₹'000)" },
        grid: { drawOnChartArea: false },
      },
    },
  };

  // Chart.js data & options for Revenue Trend (Line)
  const lineChartData = {
    labels: weekelyOrderData.map((d) => d.name),
    datasets: [
      {
        label: "Revenue",
        data: weekelyOrderData.map((d) => d.revenue),
        borderColor: "#206bc4",
        backgroundColor: "rgba(32, 107, 196, 0.2)",
        tension: 0.4,
        fill: true,
        pointBackgroundColor: "#206bc4",
        pointBorderColor: "#fff",
        pointRadius: 4,
      },
    ],
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => `₹${context.parsed.y.toLocaleString()}`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: { display: true, text: "Revenue (₹)" },
      },
    },
  };

  useEffect(() => {
    performRequest("GET", "pmg/epg-analytics/get-header-section-analytics")
      .then((res) => {
        if (res.status === 200) {
          console.log("Header Analytics:", res.data);
          // Here you would typically set state with the fetched data
          setHeaderStats(res.data);
          // setOrderData(res.data.orderData);
          // setRecentOrders(res.data.recentOrders);
          // setTopProducts(res.data.topProducts);
        } else {
          console.error("Failed to fetch analytics data");
        }
      })
      .catch((err) => {
        console.error("Error fetching analytics data:", err);
      });
  }, []);
  useEffect(() => {
    performRequest("GET", "pmg/epg-analytics/get-recent-orders")
      .then((res) => {
        if (res.status === 200) {
          setRecentOrders(res.data);
        } else {
          console.error("Failed to fetch analytics data");
        }
      })
      .catch((err) => {
        console.error("Error fetching analytics data:", err);
      });
  }, []);
  useEffect(() => {
    performRequest("GET", "pmg/epg-analytics/get-weekly-revenue")
      .then((res) => {
        if (res.status === 200) {
          setWeeklyOrderData(res?.data);
        } else {
          console.error("Failed to fetch analytics data");
        }
      })
      .catch((err) => {
        console.error("Error fetching analytics data:", err);
      });
  }, []);
  useEffect(() => {
    performRequest("GET", "pmg/epg-analytics/get-top-selling-products")
      .then((res) => {
        if (res.status === 200) {
          setTopProducts(res?.data);
        } else {
          console.error("Failed to fetch analytics data");
        }
      })
      .catch((err) => {
        console.error("Error fetching analytics data:", err);
      });
  }, []);

  return (
    <div className="page">
      {/* Sidebar – same as before */}

      <div className="page-wrapper">
        <div className="page-header d-print-none">
          <div className="container-xl">
            <div className="row g-2 align-items-center">
              <div className="col">
                <h2 className="page-title">Dashboard</h2>
              </div>
              <div className="col-auto ms-auto d-print-none">
                <div className="btn-list">
                  <button className="btn btn-outline-primary">
                    Download Report
                  </button>
                  <button className="btn btn-primary">New Product</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="page-body">
          <div className="container-xl">
            {/* Stats Cards */}
            <div className="row row-cards">
              <div className="col-sm-6 col-lg-3">
                <div className="card card-sm">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className={`me-3  red-500`}>
                        {/* <item.icon size={32} stroke={1.5} /> */}
                      </div>
                      <div>
                        <div className="text-muted">Total Stores</div>
                        <div className="h2 mb-0">{headerStats?.stores}</div>
                        {/* <div
                          className={`text-${
                            item.trend.startsWith("+") ? "success" : "danger"
                          } small`}
                        >
                          {item.trend} this week
                        </div> */}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-sm-6 col-lg-3">
                <div className="card card-sm">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className={`me-3 red-500`}>
                        {/* <item.icon size={32} stroke={1.5} /> */}
                      </div>
                      <div>
                        <div className="text-muted">Total Orders</div>
                        <div className="h2 mb-0">
                          {headerStats?.totalOrders}
                        </div>
                        {/* <div
                          className={`text-${
                            item.trend.startsWith("+") ? "success" : "danger"
                          } small`}
                        >
                          {item.trend} this week
                        </div> */}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-sm-6 col-lg-3">
                <div className="card card-sm">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className={`me-3 red-500`}>
                        {/* <item.icon size={32} stroke={1.5} /> */}
                      </div>
                      <div>
                        <div className="text-muted">Revenue</div>
                        <div className="h2 mb-0">
                          {headerStats?.totalRevenue}
                        </div>
                        {/* <div
                          className={`text-${
                            item.trend.startsWith("+") ? "success" : "danger"
                          } small`}
                        >
                          {item.trend} this week
                        </div> */}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-sm-6 col-lg-3">
                <div className="card card-sm">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className={`me-3 red-500`}>
                        {/* <item.icon size={32} stroke={1.5} /> */}
                      </div>
                      <div>
                        <div className="text-muted">Pending Orders</div>
                        <div className="h2 mb-0">
                          {headerStats?.pendingOrders}
                        </div>
                        {/* <div
                          className={`text-${
                            item.trend.startsWith("+") ? "success" : "danger"
                          } small`}
                        >
                          {item.trend} this week
                        </div> */}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Charts – now using Chart.js */}
            <div className="row row-deck row-cards mt-2">
              <div className="col-lg-8">
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">Weekly Orders & Revenue</h3>
                  </div>
                  <div className="card-body" style={{ height: "360px" }}>
                    <Bar data={barChartData} options={barOptions} />
                  </div>
                </div>
              </div>

              <div className="col-lg-4">
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">Revenue Trend</h3>
                  </div>
                  <div className="card-body" style={{ height: "360px" }}>
                    <Line data={lineChartData} options={lineOptions} />
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Orders + Top Products */}
            <div className="row row-cards mt-2">
              <div className="col-12 col-lg-7">
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">Recent Orders</h3>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-vcenter card-table">
                      <thead>
                        <tr>
                          <th>Order</th>
                          <th>Customer</th>
                          <th>Status</th>
                          <th className="w-1 text-end">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentOrders.map((order) => (
                          <tr key={order?.id}>
                            <td className="text-nowrap">
                              {order?.orderNumber || order?.id || "N/A"}
                            </td>
                            <td>{order?.user?.username}</td>
                            <td>
                              <span
                                className={`badge ${getStatusBadgeClass(
                                  order?.orderStatus
                                )} text-white`}
                              >
                                {order.orderStatus.charAt(0).toUpperCase() +
                                  order.orderStatus.slice(1)}
                              </span>
                            </td>
                            <td className="text-end">{order?.totalAmount}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="col-12 col-lg-5">
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">Top Selling Products</h3>
                  </div>
                  <div className="list-group list-group-flush list-group-hoverable">
                    {topProducts.map((p, i) => (
                      <div key={i} className="list-group-item">
                        <div className="row align-items-center">
                          <div className="col text-truncate">
                            <strong>{p?.product?.title}</strong>
                            <small className="d-block text-muted">
                              {p?.totalSold} sold
                            </small>
                          </div>
                          <div className="col-auto text-end text-green">
                            <img
                              src={p?.product?.image}
                              alt={p?.product?.title}
                              className="rounded"
                              style={{
                                width: "40px",
                                height: "40px",
                                objectFit: "cover",
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Quick alerts */}
            <div className="row row-cards mt-2">
              <div className="col-sm-6 col-lg-4">
                <div className="card bg-warning-lt">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="me-3">
                        {/* <IconAlertCircle size={32} className="text-warning" /> */}
                      </div>
                      <div>
                        <h3 className="mb-1">Low Stock Alert</h3>
                        <div className="text-muted">
                          8 products below 10 units
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-sm-6 col-lg-4">
                <div className="card text-center">
                  <div className="card-body">
                    <div className="h1 mb-1">+47</div>
                    <div className="text-muted">New Customers this week</div>
                  </div>
                </div>
              </div>

              <div className="col-sm-6 col-lg-4">
                <div className="card bg-success-lt text-center">
                  <div className="card-body">
                    <div className="h1 mb-1">+24%</div>
                    <div className="text-muted">Revenue growth</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
